import * as shared from '@aloft/toolkit-shared';
import { expected } from '@aloft/toolkit-shared';
import { Flex } from '@aloft/toolkit-sui';

import { useEffect, useLayoutEffect } from 'react';
import { Outlet, useLocation, useParams } from 'react-router';

import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary.tsx';
import { GuidedTours } from '../../components/GuidedTour/GuidedTours.tsx';
import { Tkv2ReferenceBar } from '../../components/ReferenceBar/Tkv2ReferenceBar.tsx';
import { ToolkitGenericFallback } from '../../components/ToolkitGenericFallback.tsx';
import { useAlertToastHoverEmphasis } from '../../hooks/useAlertToastHoverEmphasis.ts';
import { useAlertToastHoverSync } from '../../hooks/useAlertToastHoverSync.ts';
import { useSyncTriggeredAlertsToCurrentRoute } from '../../hooks/useSyncTriggeredAlertsToCurrentRoute.ts';
import { useTriggeredAlertToasts } from '../../hooks/useTriggeredAlertToasts.ts';
import { useAppraisifyStopAndCall } from '../../hooks/validation/useAppraisifyStopAndCall.tsx';
import { MainLayout } from '../../layout/MainLayout.tsx';
import { logger } from '../../logging/logging.ts';
import { usePriorSalesSync } from '../../modules/appraisifyCommentary/usePriorSalesSync.tsx';
import { AlertsProvider } from '../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { Tkv2ReferenceViewDrawer } from '../../modules/referenceView/Tkv2ReferenceViewDrawer.tsx';
import { getReferenceProperty } from '../../modules/utils/getReferenceProperty.ts';
import { startOrder } from '../../redux/features/orderSlice.ts';
import {
  clearAlertsPanelAutoOpenedForNavBlock,
  setProperties,
  setReferenceViewOpen,
} from '../../redux/features/referenceBarUISlice.ts';
import {
  selectCompSequence,
  selectIsInspectifyOrder,
  selectStartedAt,
} from '../../redux/features/selectors/orderSelectors.ts';
import { selectSubjectAddress } from '../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectReferenceBarUI } from '../../redux/features/selectors/referenceBarUISelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { OrderSyncDriver } from '../OrderSync/OrderSyncDriver.tsx';
import { Tkv2ControllerReferenceBarContext } from '../Tkv2ControllerReferenceBarContext.ts';
import { useNewTkv2ReferenceBarUserInterface } from '../hooks/useNewTkv2ReferenceBarUserInterface.ts';

const log = logger();

export const CraftsmanOrderRootElement = () => {
  const { orderId } = useParams();
  const referenceBarUI = useNewTkv2ReferenceBarUserInterface();

  (window as any).shared = shared;

  return (
    <OrderSyncDriver orderID={expected(orderId)}>
      <Tkv2ControllerReferenceBarContext.Provider value={referenceBarUI}>
        <AlertsProvider>
          <Root />
        </AlertsProvider>
      </Tkv2ControllerReferenceBarContext.Provider>
    </OrderSyncDriver>
  );
};

const Root = () => {
  const subjectAddress = useAppSelector(selectSubjectAddress);
  const dispatch = useAppDispatch();
  const compSequence = useAppSelector(selectCompSequence);
  const startedAt = useAppSelector(selectStartedAt);
  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const { stopAndCallModal } = useAppraisifyStopAndCall();
  useTriggeredAlertToasts();
  useAlertToastHoverSync();
  useAlertToastHoverEmphasis();
  useSyncTriggeredAlertsToCurrentRoute();
  usePriorSalesSync();

  const { pathname } = useLocation();
  const { alertsPanelAutoOpenedForNavBlock } = useAppSelector(selectReferenceBarUI);
  useLayoutEffect(() => {
    if (alertsPanelAutoOpenedForNavBlock) {
      dispatch(clearAlertsPanelAutoOpenedForNavBlock());
      return;
    }
    dispatch(setReferenceViewOpen(false));
  }, [alertsPanelAutoOpenedForNavBlock, dispatch, pathname]);

  useEffect(() => {
    const comparableProperties = compSequence.map((compId) =>
      getReferenceProperty(compId, compSequence),
    );

    dispatch(setProperties(comparableProperties));
  }, [dispatch, compSequence]);

  useEffect(() => {
    if (!startedAt && isInspectifyOrder) {
      dispatch(startOrder());
    }
  }, [startedAt]);

  return (
    <MainLayout title={subjectAddress}>
      <Flex height="100%" width="100%">
        <ErrorBoundary
          Fallback={ToolkitGenericFallback}
          onError={() => log.fatal('ToolkitControllerOrderGlobal failure')}
        >
          <Tkv2ReferenceViewDrawer>
            <Outlet />
          </Tkv2ReferenceViewDrawer>
          <Tkv2ReferenceBar />
          <GuidedTours />
          {stopAndCallModal()}
        </ErrorBoundary>
      </Flex>
    </MainLayout>
  );
};
